/**
 * Cart Upsells - Spectify
 * Handles upsell add-to-cart and variant selection
 * Fetches variant data via Shopify Product API
 */

(function() {
  'use strict';

  let modalOverlay = null;
  let modalContainer = null;
  let currentProductData = null;

  function createModalElements() {
    if (modalOverlay) return;

    modalOverlay = document.createElement('div');
    modalOverlay.className = 'cart-upsell-variant-overlay';
    modalOverlay.addEventListener('click', closeVariantModal);
    document.body.appendChild(modalOverlay);

    modalContainer = document.createElement('div');
    modalContainer.className = 'cart-upsell-variant-modal';
    document.body.appendChild(modalContainer);
  }

  async function openVariantModal(productUrl) {
    createModalElements();
    
    // Show loading state
    modalContainer.innerHTML = '<div style="padding: 2rem; text-align: center;">Laden...</div>';
    modalOverlay.classList.add('is-active');
    modalContainer.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    try {
      // Fetch product data from Shopify API
      const response = await fetch(productUrl);
      if (!response.ok) throw new Error('Failed to fetch product');
      
      const data = await response.json();
      currentProductData = data.product;
      
      // Build and show modal
      renderVariantModal(currentProductData);
      
    } catch (error) {
      console.error('Error loading product:', error);
      modalContainer.innerHTML = '<div style="padding: 2rem; text-align: center;">Er ging iets mis. Probeer opnieuw.</div>';
    }
  }

  function renderVariantModal(product) {
    const price = formatMoney(product.price);
    const comparePrice = product.compare_at_price ? formatMoney(product.compare_at_price) : null;
    const imageUrl = product.featured_image ? product.featured_image.replace(/(\.[^\.]+)$/, '_120x$1') : '';
    
    let optionsHtml = '';
    product.options.forEach((option, index) => {
      const values = [...new Set(product.variants.map(v => v.options[index]))];
      optionsHtml += `
        <div class="cart-upsell-variant-selector__option">
          <label class="cart-upsell-variant-selector__label">${option}</label>
          <select class="cart-upsell-variant-selector__select" data-option-index="${index}">
            <option value="">Selecteer ${option.toLowerCase()}</option>
            ${values.map(v => `<option value="${v}">${v}</option>`).join('')}
          </select>
        </div>
      `;
    });

    modalContainer.innerHTML = `
      <div class="cart-upsell-variant-selector">
        <div class="cart-upsell-variant-selector__header">
          <div class="cart-upsell-variant-selector__product">
            ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="cart-upsell-variant-selector__image" width="60" height="60">` : ''}
            <div class="cart-upsell-variant-selector__details">
              <h4 class="cart-upsell-variant-selector__title">${product.title}</h4>
              <div class="cart-upsell-variant-selector__price">
                ${price}
                ${comparePrice ? `<del>${comparePrice}</del>` : ''}
              </div>
            </div>
          </div>
          <button type="button" class="cart-upsell-variant-selector__close" data-close-variant-selector aria-label="Sluiten">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div class="cart-upsell-variant-selector__options">
          ${optionsHtml}
        </div>
        
        <button type="button" class="cart-upsell-variant-selector__submit" data-add-selected-variant disabled>
          Toevoegen aan winkelwagen
        </button>
      </div>
    `;

    // Initialize event listeners
    initModalEventListeners();
  }

  function initModalEventListeners() {
    const closeButton = modalContainer.querySelector('[data-close-variant-selector]');
    const selects = modalContainer.querySelectorAll('.cart-upsell-variant-selector__select');
    const submitButton = modalContainer.querySelector('[data-add-selected-variant]');

    if (closeButton) {
      closeButton.addEventListener('click', closeVariantModal);
    }

    selects.forEach(select => {
      select.addEventListener('change', () => {
        const selectedOptions = Array.from(selects).map(s => s.value);
        const allSelected = selectedOptions.every(opt => opt !== '');
        
        if (allSelected && currentProductData) {
          // Find matching variant
          const matchingVariant = currentProductData.variants.find(variant => {
            return selectedOptions.every((opt, index) => variant.options[index] === opt);
          });

          if (matchingVariant && matchingVariant.available) {
            submitButton.disabled = false;
            submitButton.dataset.variantId = matchingVariant.id;
          } else {
            submitButton.disabled = true;
            submitButton.dataset.variantId = '';
            if (matchingVariant && !matchingVariant.available) {
              submitButton.textContent = 'Niet op voorraad';
            }
          }
        } else {
          submitButton.disabled = true;
          submitButton.textContent = 'Toevoegen aan winkelwagen';
        }
      });
    });

    submitButton.addEventListener('click', async () => {
      const variantId = submitButton.dataset.variantId;
      if (!variantId) return;

      submitButton.disabled = true;
      submitButton.textContent = 'Toevoegen...';

      try {
        await addToCart(variantId);
        closeVariantModal();
        refreshCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
        submitButton.textContent = 'Er ging iets mis';
        setTimeout(() => {
          submitButton.textContent = 'Toevoegen aan winkelwagen';
          submitButton.disabled = false;
        }, 2000);
      }
    });
  }

  function closeVariantModal() {
    if (modalOverlay) modalOverlay.classList.remove('is-active');
    if (modalContainer) modalContainer.classList.remove('is-active');
    document.body.style.overflow = '';
    currentProductData = null;
  }

  function formatMoney(cents) {
    // Simple euro formatting - adjust if needed
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + amount;
  }

  async function addToCart(variantId, quantity = 1) {
    const response = await fetch(KROWN.settings.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId),
          quantity: quantity
        }]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    return response.json();
  }

  function refreshCart() {
    fetch('?section_id=helper-cart')
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.text();
      })
      .then(text => {
        const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
        
        // Update cart form
        const cartFormSource = sectionInnerHTML.getElementById('AjaxCartForm');
        const cartFormTarget = document.getElementById('AjaxCartForm');
        
        if (cartFormSource && cartFormTarget) {
          cartFormTarget.innerHTML = cartFormSource.innerHTML;
          if (typeof cartFormTarget.ajaxifyCartItems === 'function') {
            cartFormTarget.ajaxifyCartItems();
          }
        }

        // Update cart subtotal
        const cartSubtotalSource = sectionInnerHTML.getElementById('AjaxCartSubtotal');
        const cartSubtotalTarget = document.getElementById('AjaxCartSubtotal');
        
        if (cartSubtotalSource && cartSubtotalTarget) {
          cartSubtotalTarget.innerHTML = cartSubtotalSource.innerHTML;
        }

        // Update cart count in header
        const newCount = sectionInnerHTML.querySelector('[data-cart-count]');
        if (newCount) {
          document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
            elm.textContent = newCount.textContent;
          });
        }

        // Update cart total in header
        const newTotal = sectionInnerHTML.querySelector('[data-cart-total]');
        if (newTotal) {
          document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
            elm.textContent = newTotal.textContent;
          });
        }

        // Update sidebar
        const sidebar = document.getElementById('site-cart-sidebar');
        if (sidebar && newCount) {
          sidebar.setAttribute('data-cart-items', newCount.textContent);
          if (parseInt(newCount.textContent) === 0) {
            sidebar.classList.add('cart-is-empty');
          } else {
            sidebar.classList.remove('cart-is-empty');
          }
        }

        // Re-init upsell buttons
        setTimeout(initUpsellButtons, 100);
        
        // Dispatch event
        const event = new CustomEvent('cart-updated');
        document.dispatchEvent(event);
        if (cartFormTarget) {
          cartFormTarget.dispatchEvent(event);
        }
      })
      .catch(error => {
        console.error('Cart refresh error:', error);
        window.location.reload();
      });
  }

  function initUpsellButtons() {
    // Direct add to cart buttons
    document.querySelectorAll('[data-upsell-add-to-cart]').forEach(button => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.classList.add('is-loading');
        button.disabled = true;
        const originalText = button.textContent;

        try {
          await addToCart(variantId);
          button.textContent = 'Toegevoegd!';
          refreshCart();
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Fout - probeer opnieuw';
          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('is-loading');
            button.disabled = false;
          }, 2000);
        }
      });
    });

    // Variant selector buttons (open modal)
    document.querySelectorAll('[data-upsell-select-variant]').forEach(button => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productUrl = button.dataset.productUrl;
        if (productUrl) {
          openVariantModal(productUrl);
        }
      });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellButtons);
  } else {
    initUpsellButtons();
  }

  // Re-initialize after cart updates
  document.addEventListener('cart-updated', () => {
    setTimeout(initUpsellButtons, 150);
  });

  // Watch for cart sidebar being shown (multiple methods for reliability)
  function setupCartSidebarObserver() {
    const cartSidebar = document.querySelector('[data-js-site-cart-sidebar]');
    if (!cartSidebar) {
      // Retry if sidebar doesn't exist yet
      setTimeout(setupCartSidebarObserver, 500);
      return;
    }

    // Method 1: Watch aria-hidden attribute
    const attrObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          if (mutation.attributeName === 'aria-hidden' && cartSidebar.getAttribute('aria-hidden') === 'false') {
            setTimeout(initUpsellButtons, 150);
          }
          if (mutation.attributeName === 'style' && cartSidebar.style.display !== 'none') {
            setTimeout(initUpsellButtons, 150);
          }
        }
      });
    });
    attrObserver.observe(cartSidebar, { attributes: true });

    // Method 2: Watch for content changes inside cart form
    const cartForm = document.getElementById('AjaxCartForm');
    if (cartForm) {
      const contentObserver = new MutationObserver(() => {
        setTimeout(initUpsellButtons, 150);
      });
      contentObserver.observe(cartForm, { childList: true, subtree: true });
    }
  }

  setupCartSidebarObserver();

  // Method 3: Override theme's refreshCart to ensure our init runs after
  const originalRefreshCart = window.refreshCart;
  if (typeof originalRefreshCart === 'function') {
    window.refreshCart = function() {
      originalRefreshCart.apply(this, arguments);
      setTimeout(initUpsellButtons, 300);
    };
  }

  // Method 4: Listen for sidebar show method being called
  const sidebarElement = document.querySelector('[data-js-site-cart-sidebar]');
  if (sidebarElement && typeof sidebarElement.show === 'function') {
    const originalShow = sidebarElement.show.bind(sidebarElement);
    sidebarElement.show = function() {
      originalShow();
      setTimeout(initUpsellButtons, 200);
    };
  }

  // Expose for external use
  window.CartUpsells = {
    init: initUpsellButtons,
    refresh: refreshCart,
    addToCart: addToCart
  };

})();

// Hide cart badge when count is 0 (only for header button)
function updateCartBadgeVisibility() {
  document.querySelectorAll('[data-header-cart-count]').forEach(badge => {
    if (badge.closest('.button--cart-handle')) {
      const count = parseInt(badge.textContent) || 0;
      badge.style.display = count === 0 ? 'none' : 'flex';
    }
  });
}

// Run on load
document.addEventListener('DOMContentLoaded', () => {
  updateCartBadgeVisibility();
  
  // Watch for changes to cart count
  document.querySelectorAll('[data-header-cart-count]').forEach(badge => {
    new MutationObserver(updateCartBadgeVisibility).observe(badge, {
      childList: true,
      characterData: true,
      subtree: true
    });
  });
});